import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { cva, VariantProps } from 'class-variance-authority';
import classNames from 'classnames';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  ParagraphNode,
} from 'lexical';
import { useEffect, useRef } from 'react';
import { ControllerRenderProps, FieldError } from 'react-hook-form';
import { ParameterDto } from '@/Domain/api/Action/dto/Action.dto';
import { parseValueToEditor } from '@/Domain/components/Action/Lexical/helper/parseValueToEditor';
import { BadgeNode } from '@/Domain/components/Action/Lexical/node/BadgeNode';
import { InsertBadgePlugin } from '@/Domain/components/Action/Lexical/plugin/InsertBadgePlugin';
import { OnChangePlugin } from '@/Domain/components/Action/Lexical/plugin/OnChangePlugin';
import { useStableParameters } from '@/Domain/hooks/Action/useStableParameters.hook';
import { cn } from '@/Domain/utils/cn';

const inputVariants = cva('focus:outline-none', {
  variants: {
    size: {
      xs: 'h-[28px] py-1 px-1.5 text-sm',
      sm: 'h-[34px] py-2 pl-4 pr-0.5 text-sm',
      md: 'h-[41px] py-2.5 pl-4 pr-1.5 text-sm',
      lg: 'h-[50px] py-3.5 px-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type Props = VariantProps<typeof inputVariants> & {
  field: ControllerRenderProps<any, string>;
  parameters?: ParameterDto[];
  placeholder?: string;
  error?: FieldError;
  withInsertBadge?: boolean;
};

export const InlineEditor = (props: Props) => {
  const config = {
    namespace: 'editable-inline-input',
    onError: (error: Error) => console.error('Lexical error:', error?.message || error),
    theme: {},
    nodes: [BadgeNode],
  };

  return (
    <LexicalComposer initialConfig={config}>
      <EditableInlineInput {...props} />
    </LexicalComposer>
  );
};

export function EditableInlineInput({
  field,
  parameters,
  placeholder,
  error,
  size,
  withInsertBadge = true,
}: Props) {
  const [editor] = useLexicalComposerContext();
  const stableParameters = useStableParameters(parameters);

  const ref = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const paragraph = $createParagraphNode();
      const parts = parseValueToEditor(field.value, stableParameters);

      for (const part of parts) {
        if (typeof part === 'string') {
          paragraph.append($createTextNode(part));
        } else {
          paragraph.append(BadgeNode.importJSON(part));
        }
      }

      root.append(paragraph);
    });
  }, [editor]);

  useEffect(() => {
    BadgeNode.parameters = stableParameters;

    requestAnimationFrame(() => {
      editor.update(() => {
        const root = $getRoot();

        for (const paragraph of root.getChildren()) {
          if (paragraph.getType() !== 'paragraph') continue;

          for (const node of (paragraph as ParagraphNode).getChildren()) {
            if (node.getType() === 'badge') {
              const badge = node as BadgeNode;
              badge.replace(BadgeNode.cloneWithoutKey(badge));
            }
          }
        }
      });
    });
  }, [stableParameters, editor]);

  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (event) {
            event.preventDefault();
          }
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor],
  );

  return (
    <div
      className={classNames(
        cn(inputVariants({ size })),
        'flex w-full items-center overflow-hidden rounded-xl border border-input bg-card text-sm text-foreground outline-none focus-within:!ring-[3px]',
        {
          'focus-within:!border-destructive !border-destructive text-destructive focus-within:!ring-destructive/20':
            error,
          'focus-within:!border-foreground focus-within:ring-ring/25': !error,
        },
      )}
    >
      <div className="flex-1 relative min-w-0">
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              ref={ref}
              className="
                w-full
                block
                outline-none
                overflow-x-auto
                !whitespace-nowrap
                hide-scrollbar
              "
            />
          }
          placeholder={
            <div className="pointer-events-none absolute inset-0 select-none px-0.5 text-foreground-subtle">
              {placeholder}
            </div>
          }
          ErrorBoundary={({ children }) => <>{children}</>}
        />

        <OnChangePlugin onChange={field.onChange} />
      </div>

      {withInsertBadge && <InsertBadgePlugin parameters={stableParameters} />}
    </div>
  );
}
